/**
 * Incident Report Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { AlertTriangle, Save, Shield } from "lucide-react";
import { toast } from "sonner";
import SpeechDictation from "../SpeechDictation";

export function IncidentReportOverlay({ patientId, patientName, onSave, onClose }) {
  const [formData, setFormData] = useState({
    incident_type: "",
    date: "",
    time: "",
    location: "",
    witnesses: "",
    description: "",
    immediate_actions: "",
    injuries: "",
    injury_severity: "",
    physician_notified: false,
    physician_name: "",
    family_notified: false,
    family_member: "",
    preventive_measures: "",
    reporter_name: "",
  });

  const handleSave = () => {
    if (!formData.incident_type || !formData.description) {
      toast.error("Please fill in incident type and description");
      return;
    }
    toast.success("Incident report submitted");
    if (onSave) onSave(formData);
    if (onClose) onClose();
  };

  return (
    <div className="space-y-4 p-4" data-testid="incident-report-overlay">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="w-5 h-5 text-red-400" />
        <h3 className="font-semibold text-white">Incident Report</h3>
        <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
      </div>

      <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-red-400" />
          <p className="text-sm text-red-300">All incidents must be reported within 24 hours per policy</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Incident Type</Label>
          <Select value={formData.incident_type} onValueChange={(v) => setFormData({...formData, incident_type: v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Select type..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fall">Fall</SelectItem>
              <SelectItem value="medication_error">Medication Error</SelectItem>
              <SelectItem value="skin_injury">Skin Injury/Wound</SelectItem>
              <SelectItem value="equipment">Equipment Malfunction</SelectItem>
              <SelectItem value="behavioral">Behavioral Incident</SelectItem>
              <SelectItem value="elopement">Elopement Risk</SelectItem>
              <SelectItem value="complaint">Patient/Family Complaint</SelectItem>
              <SelectItem value="near_miss">Near Miss</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Location</Label>
          <Select value={formData.location} onValueChange={(v) => setFormData({...formData, location: v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Where occurred..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bedroom">Bedroom</SelectItem>
              <SelectItem value="bathroom">Bathroom</SelectItem>
              <SelectItem value="living_area">Living Area</SelectItem>
              <SelectItem value="hallway">Hallway</SelectItem>
              <SelectItem value="outside">Outside</SelectItem>
              <SelectItem value="facility_room">Facility Room</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Date</Label>
          <Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="bg-slate-800 border-slate-700 text-white" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Time</Label>
          <Input type="time" value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} className="bg-slate-800 border-slate-700 text-white" />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Witnesses (if any)</Label>
        <Input 
          value={formData.witnesses} 
          onChange={(e) => setFormData({...formData, witnesses: e.target.value})} 
          placeholder="Names of witnesses" 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-slate-200">Description of Incident</Label>
          <SpeechDictation onTranscript={(t) => setFormData({...formData, description: formData.description + " " + t})} buttonSize="sm" />
        </div>
        <Textarea 
          value={formData.description} 
          onChange={(e) => setFormData({...formData, description: e.target.value})} 
          placeholder="Describe what happened in detail..." 
          rows={3} 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Immediate Actions Taken</Label>
        <Textarea 
          value={formData.immediate_actions} 
          onChange={(e) => setFormData({...formData, immediate_actions: e.target.value})} 
          placeholder="What was done immediately..." 
          rows={2} 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Injuries Sustained</Label>
          <Input 
            value={formData.injuries} 
            onChange={(e) => setFormData({...formData, injuries: e.target.value})} 
            placeholder="Describe any injuries" 
            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Severity</Label>
          <Select value={formData.injury_severity} onValueChange={(v) => setFormData({...formData, injury_severity: v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Severity..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Injury</SelectItem>
              <SelectItem value="minor">Minor</SelectItem>
              <SelectItem value="moderate">Moderate</SelectItem>
              <SelectItem value="serious">Serious</SelectItem>
              <SelectItem value="death">Death</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
          <div>
            <Label className="text-slate-200">Physician Notified?</Label>
            <p className="text-xs text-slate-400">Was MD contacted?</p>
          </div>
          <Switch checked={formData.physician_notified} onCheckedChange={(c) => setFormData({...formData, physician_notified: c})} />
        </div>
        <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
          <div>
            <Label className="text-slate-200">Family Notified?</Label>
            <p className="text-xs text-slate-400">Was family contacted?</p>
          </div>
          <Switch checked={formData.family_notified} onCheckedChange={(c) => setFormData({...formData, family_notified: c})} />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Preventive Measures</Label>
        <Textarea 
          value={formData.preventive_measures} 
          onChange={(e) => setFormData({...formData, preventive_measures: e.target.value})} 
          placeholder="What will be done to prevent recurrence..." 
          rows={2} 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Reporter Name</Label>
        <Input 
          value={formData.reporter_name} 
          onChange={(e) => setFormData({...formData, reporter_name: e.target.value})} 
          placeholder="Your name" 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
        <Button onClick={handleSave} className="bg-red-600 hover:bg-red-700"><Save className="w-4 h-4 mr-1" />Submit Report</Button>
      </div>
    </div>
  );
}

export default IncidentReportOverlay;
